import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import {
  getSubscription,
  getCreditBalance,
  getPlanPrices,
  getPlanCatalog,
  changePlan,
  createCheckoutSession,
  createPortalSession,
  cancelSubscription,
  resumeSubscription,
  getReferral,
  validatePromoCode,
} from './api';
import type { BillingInterval, SubscriptionStatus } from './types';

interface CheckoutParams {
  planCode?: string;
  interval?: BillingInterval;
  promotionCode?: string;
}

function subKey(workspaceId: string | null) {
  return ['billing', 'subscription', workspaceId] as const;
}
function creditsKey(workspaceId: string | null) {
  return ['billing', 'credits', workspaceId] as const;
}

/**
 * The app's QueryClient, registered at provider setup so non-React code paths
 * (the entity-event handler, the assistant transport) can invalidate the
 * balance when work that spends credits finishes. Nothing else refreshes this
 * query — without it the meter shows a stale number until Settings is reopened.
 */
let billingQueryClient: QueryClient | null = null;

export function registerBillingQueryClient(client: QueryClient | null): void {
  billingQueryClient = client;
}

/** Refetch the credit balance for a workspace. No-op before registration. */
export function invalidateCreditBalance(workspaceId: string | null): Promise<void> {
  if (!billingQueryClient) return Promise.resolve();
  return billingQueryClient.invalidateQueries({
    queryKey: creditsKey(workspaceId),
    refetchType: 'all',
  });
}

/**
 * Subscription for a workspace. Pass an explicit `workspaceId` to read a
 * SPECIFIC workspace (e.g. the personal subscription tab reading the personal
 * workspace while a team is active); omit it to follow the active workspace.
 */
export function useSubscription(workspaceId?: string) {
  const activeId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const id = workspaceId ?? activeId;
  return useQuery({
    queryKey: subKey(id),
    queryFn: () => getSubscription(id ?? undefined),
    enabled: id !== null,
  });
}

export function useCreditBalance(workspaceId?: string) {
  const activeId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const id = workspaceId ?? activeId;
  return useQuery({
    queryKey: creditsKey(id),
    queryFn: () => getCreditBalance(id ?? undefined),
    enabled: id !== null,
  });
}

export function usePlanPrices(planCode = 'pro') {
  return useQuery({
    queryKey: ['billing', 'plan-prices', planCode] as const,
    queryFn: () => getPlanPrices(planCode),
    staleTime: 5 * 60 * 1000,
  });
}

/** Catalog scoped to the workspace type — team workspaces see the team tiers, personal see basic/plus/pro. */
export function usePlanCatalog(workspaceType: 'personal' | 'team' = 'personal') {
  return useQuery({
    queryKey: ['billing', 'plan-catalog', workspaceType] as const,
    queryFn: () => getPlanCatalog({ workspaceType }),
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch a single plan by code (e.g. 'teams_pro'), including plans excluded from the personal catalog. */
export function usePlanByCode(planCode: string) {
  return useQuery({
    queryKey: ['billing', 'plan', planCode] as const,
    queryFn: () => getPlanCatalog({ planCode }),
    staleTime: 5 * 60 * 1000,
    select: (data) => data.plans[0],
  });
}

// Each mutation hook accepts an optional `workspaceId` so a tab scoped to a
// SPECIFIC workspace (e.g. the personal subscription tab while a team is active)
// targets that workspace, not the active one. Omit it to follow the active
// workspace (the workspace/team billing tab does this).
export function useChangePlan(workspaceId?: string) {
  return useMutation({
    mutationFn: ({ planCode, interval }: CheckoutParams) =>
      changePlan(planCode ?? 'pro', interval, workspaceId),
  });
}

export function useInvalidateBilling(workspaceId?: string) {
  const qc = useQueryClient();
  const activeId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const id = workspaceId ?? activeId;
  // Returns a promise so callers can await the refetch before, e.g., closing a
  // dialog. `refetchType: 'all'` refetches even observers that briefly go
  // inactive during the post-mutation render churn (catalog → status card swap),
  // so the billing modal shows the new plan without a close/reopen.
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: subKey(id), refetchType: 'all' }),
      qc.invalidateQueries({ queryKey: creditsKey(id), refetchType: 'all' }),
    ]);
}

export function useCreateCheckoutSession(workspaceId?: string) {
  return useMutation({
    mutationFn: ({ planCode = 'pro', interval = 'month', promotionCode }: CheckoutParams = {}) =>
      createCheckoutSession(planCode, interval, workspaceId, promotionCode),
  });
}

export function useReferral() {
  return useQuery({
    queryKey: ['billing', 'referral'] as const,
    queryFn: () => getReferral(),
  });
}

/**
 * Mutation, not a query: the result must never persist across a code change or
 * an interval switch — a stale "applied" verdict at checkout would be shown as
 * fact after the input that produced it no longer matches.
 */
export function useValidatePromoCode(workspaceId?: string) {
  return useMutation({
    mutationFn: ({ code, interval }: { code: string; interval: BillingInterval }) =>
      validatePromoCode(code, interval, workspaceId),
  });
}

export function useCreatePortalSession(workspaceId?: string) {
  return useMutation({ mutationFn: () => createPortalSession(workspaceId) });
}

export function useCancelSubscription(workspaceId?: string) {
  const invalidate = useInvalidateBilling(workspaceId);
  return useMutation({
    mutationFn: () => cancelSubscription(workspaceId),
    onSuccess: () => invalidate(),
  });
}

export function useResumeSubscription(workspaceId?: string) {
  const invalidate = useInvalidateBilling(workspaceId);
  return useMutation({
    mutationFn: () => resumeSubscription(workspaceId),
    onSuccess: () => invalidate(),
  });
}

/** How long to wait for invoice.paid before telling the user it's still processing. */
export const PLAN_ACTIVATION_TIMEOUT_MS = 30_000;
export const PLAN_ACTIVATION_POLL_MS = 2_000;

export type PlanActivationState = 'idle' | 'pending' | 'activated' | 'timed_out';

/** The pre-payment fields that invoice.paid can change — compared as a whole,
 * since a same-tier interval switch (month -> year) changes none but planCode. */
export interface PrePaymentPlanSnapshot {
  planCode: SubscriptionStatus['planCode'];
  interval: BillingInterval;
  status: SubscriptionStatus['status'];
}

function samePlanSnapshot(sub: SubscriptionStatus, snapshot: PrePaymentPlanSnapshot): boolean {
  return (
    sub.planCode === snapshot.planCode &&
    sub.interval === snapshot.interval &&
    sub.status === snapshot.status
  );
}

/**
 * Wait for a paid checkout to actually take effect.
 *
 * Checkout does not bind the local row: the binding, plan upgrade and interval
 * are applied when Stripe delivers invoice.paid, which lands seconds later and
 * may be retried over minutes. A single invalidate fired when confirmPayment
 * resolves therefore refetches the PRE-payment plan, so the UI cannot announce
 * an upgrade at that moment without contradicting the server it just read.
 *
 * Poll until the plan actually changes from what it was before payment, then
 * stop. Give up at PLAN_ACTIVATION_TIMEOUT_MS so a webhook that is slow or
 * failing degrades to an honest "still processing" rather than a spinner with
 * no end.
 */
export function useAwaitPlanActivation(workspaceId?: string) {
  const qc = useQueryClient();
  const activeId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const id = workspaceId ?? activeId;
  const [state, setState] = useState<PlanActivationState>('idle');
  const previousPlan = useRef<PrePaymentPlanSnapshot | null>(null);
  const deadline = useRef(0);
  // Captured at start() so a workspace switch mid-poll can be detected — `id`
  // itself follows the active workspace on every render, so comparing against
  // it directly would silently repoint the poll at the new workspace.
  const activationWorkspaceId = useRef<string | null>(null);

  const start = useCallback(
    (snapshot: PrePaymentPlanSnapshot) => {
      previousPlan.current = snapshot;
      activationWorkspaceId.current = id ?? null;
      deadline.current = Date.now() + PLAN_ACTIVATION_TIMEOUT_MS;
      setState('pending');
    },
    [id],
  );

  const reset = useCallback(() => {
    previousPlan.current = null;
    activationWorkspaceId.current = null;
    setState('idle');
  }, []);

  useEffect(() => {
    if (state !== 'pending') return;
    // The workspace changed after start() captured it — this purchase is no
    // longer the one on screen, so neither activating nor timing out against
    // the new workspace's plan would be honest. SettingsModal stays mounted
    // outside the keyed SyncSystemProvider, so this hook's instance survives
    // the switch — leaving `state` at 'pending' would strand the new
    // workspace's tab rendering neither the catalog nor the status card
    // forever. Clear to 'idle'; a fresh start() on the new workspace (or
    // navigating back to this one) begins its own poll.
    if (id !== activationWorkspaceId.current) {
      setState('idle');
      return;
    }
    const pollWorkspaceId = activationWorkspaceId.current;
    const snapshot = previousPlan.current;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const sub = await getSubscription(pollWorkspaceId ?? undefined);
        if (cancelled) return;
        if (!snapshot || !samePlanSnapshot(sub, snapshot)) {
          qc.setQueryData(subKey(pollWorkspaceId), sub);
          void qc.invalidateQueries({ queryKey: creditsKey(pollWorkspaceId), refetchType: 'all' });
          setState('activated');
          return;
        }
      } catch {
        // A failed poll is not a failed payment — keep waiting for the deadline.
      }
      if (cancelled) return;
      if (Date.now() >= deadline.current) {
        setState('timed_out');
        return;
      }
      timer = setTimeout(() => void poll(), PLAN_ACTIVATION_POLL_MS);
    };

    let timer = setTimeout(() => void poll(), 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [state, id, qc]);

  return { state, start, reset };
}
